import React, { useRef, useEffect, useState } from 'react';
import { Editor } from '@toast-ui/react-editor';
import '@toast-ui/editor/dist/toastui-editor.css';
import { uploadMarkdownImage } from '../../api/markdownImages';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../common/Toast';
import MarkdownViewer from './MarkdownViewer';
import './MarkdownEditor.scss';

// linkTickets 直接轉給預覽用的 MarkdownViewer,讓預覽跟實際渲染一致
// (工作項目已關掉 #編號 自動連結,預覽不能還長出連結)
const MarkdownEditor = ({ value = '', onChange, placeholder = '請輸入內容...', height = '400px', linkTickets = true }) => {
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const toast = useToast();
  const { profile } = useAuth();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [activeTab, setActiveTab] = useState('write'); // 'write' | 'preview'

  // 當 value prop 改變時，更新編輯器內容
  useEffect(() => {
    const editorInstance = editorRef.current?.getInstance();
    if (editorInstance) {
      const currentContent = editorInstance.getMarkdown();
      if (currentContent !== value) {
        editorInstance.setMarkdown(value || '');
      }
    }
  }, [value]);

  // 當編輯器內容改變時
  const handleChange = () => {
    const editorInstance = editorRef.current?.getInstance();
    if (editorInstance && onChange) {
      const markdown = editorInstance.getMarkdown();
      onChange(markdown);
    }
  };

  // 處理圖片上傳
  const handleImageUpload = async (files) => {
    const editorInstance = editorRef.current?.getInstance();
    if (!editorInstance) return;

    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        if (file.size > 10 * 1024 * 1024) {
          return { success: false, file, error: '檔案超過 10MB 限制' };
        }
        const result = await uploadMarkdownImage(file, profile?.department_id);
        return { success: result.success, file, url: result.url, error: result.error };
      });

      const results = await Promise.all(uploadPromises);

      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      results.forEach(({ success, file, url, error }) => {
        if (success) {
          const altText = file.name.replace(/\.[^/.]+$/, '');
          const currentMode = editorInstance.isMarkdownMode() ? 'markdown' : 'wysiwyg';
          if (currentMode === 'markdown') {
            editorInstance.insertText(`![${altText}](${url})\n`);
          } else {
            editorInstance.exec('addImage', { imageUrl: url, altText });
          }
          successCount++;
        } else {
          errorCount++;
          errors.push(`❌ ${file.name}: ${error}`);
        }
      });

      if (successCount > 0) {
        toast.success(`成功上傳 ${successCount} 張圖片${errorCount > 0 ? `，${errorCount} 張失敗` : ''}`);
      }
      if (errors.length > 0 && successCount === 0) {
        toast.error(`圖片上傳失敗: ${errors[0]}`);
      }
      handleChange();
    } catch (error) {
      console.error('圖片上傳錯誤:', error);
      toast.error('圖片上傳失敗: ' + error.message);
    }
  };

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) handleImageUpload(files);
    e.target.value = '';
  };

  // 自訂工具列
  const toolbarItems = [
    ['heading', 'bold', 'italic'],
    ['task', 'table', 'link'],
    [
      {
        el: (() => {
          const button = document.createElement('button');
          button.className = 'toastui-editor-toolbar-icons custom-image-btn';
          button.style.backgroundImage = 'none';
          button.style.margin = '0';
          button.innerHTML = '📷';
          button.title = '上傳圖片到 Supabase Storage';
          button.type = 'button';
          button.addEventListener('click', () => {
            fileInputRef.current?.click();
          });
          return button;
        })(),
        tooltip: '上傳圖片',
      },
    ],
    [
      {
        el: (() => {
          const button = document.createElement('button');
          button.className = 'toastui-editor-toolbar-icons custom-fullscreen-btn';
          button.style.backgroundImage = 'none';
          button.style.margin = '0';
          button.innerHTML = '⛶';
          button.title = '全螢幕編輯';
          button.type = 'button';
          button.addEventListener('click', () => {
            setIsFullscreen(prev => !prev);
          });
          return button;
        })(),
        tooltip: '全螢幕',
      }
    ]
  ];

  return (
    <div className={`markdown-editor-wrapper ${isFullscreen ? 'fullscreen' : ''}`}>
      {/* 自訂 Write / Preview tab */}
      <div className="me-tab-bar">
        <button
          className={`me-tab ${activeTab === 'write' ? 'active' : ''}`}
          onClick={() => setActiveTab('write')}
        >
          Write
        </button>
        <button
          className={`me-tab ${activeTab === 'preview' ? 'active' : ''}`}
          onClick={() => setActiveTab('preview')}
        >
          Preview
        </button>
      </div>

      {/* Write mode: Toast UI Editor */}
      <div style={{ display: activeTab === 'write' ? 'flex' : 'none', flex: 1, minHeight: 0, flexDirection: 'column' }}>
        <Editor
          ref={editorRef}
          initialValue={value}
          placeholder={placeholder}
          height={isFullscreen ? '100%' : height}
          initialEditType="markdown"
          useCommandShortcut={true}
          hideModeSwitch={true}
          previewStyle="tab"
          toolbarItems={toolbarItems}
          onChange={handleChange}
          linkAttributes={{
            target: '_blank',
            rel: 'noopener noreferrer',
          }}
          hooks={{
            addImageBlobHook: (blob, callback) => {
              handleImageUpload([blob]);
              return false;
            },
          }}
        />
      </div>

      {/* Preview mode: 用我們自己的 MarkdownViewer */}
      {activeTab === 'preview' && (
        <div className="me-preview">
          {value ? (
            <MarkdownViewer content={value} linkTickets={linkTickets} />
          ) : (
            <div className="me-preview-empty">沒有內容可預覽</div>
          )}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />
    </div>
  );
};

export default MarkdownEditor;
